import React, { useMemo, useState } from "react";
import { Pressable, ScrollView, Text, TextInput, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { s } from "../SustainTeamManagementScreen.styles";
import {
  TeamMember,
  SortDirection,
  SCOPE_LABEL,
  RosterFilters,
  FilterOption,
  ROLE_FILTER_OPTIONS,
  NOTIFICATION_CHANNEL_FILTER_OPTIONS,
  NOTIFICATION_SCOPE_FILTER_OPTIONS,
  WORKING_STATUS_FILTER_OPTIONS,
} from "./SustainTeamTypes";
import ChannelIcons from "./ChannelIcons";
import TeamTableSkeleton from "./TeamTableSkeleton";
import RosterColumnFilter from "./RosterColumnFilter";
import { formatUserName } from "../../../utils/emailTouserName";
import { useTheme } from "../../../theme";

interface Props {
  sortedFilteredMembers: TeamMember[];
  teamLoading: boolean;
  teamLoadError: boolean;
  filteredCount: number;
  search: string;
  setSearch: (v: string) => void;
  searchFocused: boolean;
  setSearchFocused: (v: boolean) => void;
  memberSortDirection: SortDirection;
  onToggleMemberSort: () => void;
  canEditMember: (member: TeamMember) => boolean;
  onOpenEdit: (member: TeamMember) => void;
  rosterFilters: RosterFilters;
  onRosterFiltersChange: (next: RosterFilters) => void;
  managerOptions: FilterOption[];
  teamOptions: FilterOption[];
  allTeamOptions: FilterOption[];
  isDark?: boolean;
}

const TeamTable: React.FC<Props> = ({
  sortedFilteredMembers,
  teamLoading,
  teamLoadError,
  filteredCount,
  search,
  setSearch,
  searchFocused,
  setSearchFocused,
  memberSortDirection,
  onToggleMemberSort,
  canEditMember,
  onOpenEdit,
  rosterFilters,
  onRosterFiltersChange,
  managerOptions,
  teamOptions,
  allTeamOptions,
  isDark,
}) => {
  const { colors } = useTheme();
  const [openFilterColumn, setOpenFilterColumn] = useState<keyof RosterFilters | null>(null);

  const toggleFilterColumn = (col: keyof RosterFilters) =>
    setOpenFilterColumn((prev) => (prev === col ? null : col));

  const setFilter = (col: keyof RosterFilters, next: string[]) =>
    onRosterFiltersChange({ ...rosterFilters, [col]: next });

  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    allTeamOptions.forEach((t) => map.set(t.value, t.label));
    return map;
  }, [allTeamOptions]);

  const teamColorById = useMemo(() => {
    const map = new Map<string, string>();
    allTeamOptions.forEach((t) => map.set(t.value, t.color ?? "#838383"));
    return map;
  }, [allTeamOptions]);

  const availableTeamIds = useMemo(
    () => new Set(teamOptions.map((team) => team.value)),
    [teamOptions],
  );

  const hasAppliedFilters = search.trim().length > 0 ||
    Object.values(rosterFilters).some((filterValues) => filterValues.length > 0);

  return (
  <View style={[s.tableCard, isDark && { backgroundColor: "#202020" }]}>
    {/* Toolbar */}
    <View style={[s.toolbar, isDark && { borderBottomColor: "#363636" }]}>
      <View style={[s.searchWrap, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }, searchFocused && s.searchWrapFocused, isDark && searchFocused && { backgroundColor: "#2B2B2B", borderColor: colors.brand.primary }]}>
        <MaterialIcons
          name="search"
          size={16}
          color={searchFocused ? (isDark ? "#3D8BFF" : "#034895") : (isDark ? "#6E6E6E" : "#9a9a9a")}
          style={s.searchIcon}
        />
        <TextInput
          style={[s.searchInput, isDark && { color: "#F2F2F2" }]}
          placeholder="Search by name or email"
          placeholderTextColor={isDark ? "#6E6E6E" : "#9a9a9a"}
          value={search}
          onChangeText={setSearch}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          {...({ dataSet: { nofocusring: "1" } } as any)}
        />
      </View>
      <Text style={[s.resultCount, isDark && { color: "#B0B0B0" }]}>{filteredCount} members</Text>
    </View>

    {/* Roster — columns grow to fill the full row width; scrolls horizontally only when the minimum widths don't fit */}
    <ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={s.tableContent}>
    <View style={s.tableScrollInner}>

    {/* Column headers */}
    <View style={[s.tableHead, isDark && { backgroundColor: "#1a1a1a", borderBottomColor: "#404040" }]}>
      <View style={[s.colName]}>
        <Pressable style={s.sortHeaderBtn} onPress={onToggleMemberSort}>
          <Text style={[s.th, isDark && { color: "#B0B0B0" }]}>MEMBER NAME</Text>
          <Text style={[s.sortIndicator, isDark && { color: "#6E6E6E" }]}>{memberSortDirection === "asc" ? "↑" : "↓"}</Text>
        </Pressable>
      </View>
      <View style={[s.colEmail]}><Text style={[s.th, isDark && { color: "#B0B0B0" }]}>EMAIL</Text></View>
      <View style={[s.colRole]}>
        <RosterColumnFilter
          label="ROLE"
          options={ROLE_FILTER_OPTIONS}
          selected={rosterFilters.role}
          onChange={(next) => setFilter("role", next)}
          isOpen={openFilterColumn === "role"}
          onToggle={() => toggleFilterColumn("role")}
        />
      </View>
      <View style={[s.colManager]}>
        <RosterColumnFilter
          label="MANAGER"
          options={managerOptions}
          selected={rosterFilters.manager}
          onChange={(next) => setFilter("manager", next)}
          isOpen={openFilterColumn === "manager"}
          onToggle={() => toggleFilterColumn("manager")}
          searchable
        />
      </View>
      <View style={[s.colTeam]}>
        <RosterColumnFilter
          label="TEAM"
          options={teamOptions}
          selected={rosterFilters.team}
          onChange={(next) => setFilter("team", next)}
          isOpen={openFilterColumn === "team"}
          onToggle={() => toggleFilterColumn("team")}
          searchable
        />
      </View>
      <View style={[s.colChannels, { alignItems: "center" }]}>
        <RosterColumnFilter
          label="NOTIFICATION CHANNELS"
          options={NOTIFICATION_CHANNEL_FILTER_OPTIONS}
          selected={rosterFilters.notificationChannel}
          onChange={(next) => setFilter("notificationChannel", next)}
          isOpen={openFilterColumn === "notificationChannel"}
          onToggle={() => toggleFilterColumn("notificationChannel")}
        />
      </View>
      <View style={[s.colScope]}>
        <RosterColumnFilter
          label="NOTIFICATION SCOPE"
          options={NOTIFICATION_SCOPE_FILTER_OPTIONS}
          selected={rosterFilters.notificationScope}
          onChange={(next) => setFilter("notificationScope", next)}
          isOpen={openFilterColumn === "notificationScope"}
          onToggle={() => toggleFilterColumn("notificationScope")}
        />
      </View>
      <View style={[s.colStatus, { alignItems: "center" }]}>
        <RosterColumnFilter
          label="WORKING STATUS"
          options={WORKING_STATUS_FILTER_OPTIONS}
          selected={rosterFilters.workingStatus}
          onChange={(next) => setFilter("workingStatus", next)}
          isOpen={openFilterColumn === "workingStatus"}
          onToggle={() => toggleFilterColumn("workingStatus")}
          align="right"
        />
      </View>
      <View style={[s.colActions, { alignItems: "flex-end" }]}><Text style={[s.th, isDark && { color: "#B0B0B0" }]}>ACTIONS</Text></View>
    </View>

    {/* Table body — loading / error / empty / rows */}
    {teamLoading ? (
      <TeamTableSkeleton isDark={isDark} />
    ) : teamLoadError ? (
      <View style={[s.emptyState, isDark && { backgroundColor: "#202020" }]}>
        <MaterialIcons name="error-outline" size={22} color="#C12C01" style={{ marginBottom: 8 }} />
        <Text style={[s.emptyText, { color: "#C12C01", textAlign: "center" }]}>
          Unable to load team members. Please refresh the page or try again later.
        </Text>
      </View>
    ) : sortedFilteredMembers.length === 0 ? (
      <View style={[s.emptyState, isDark && { backgroundColor: "#202020" }]}>
        <Text style={[s.emptyText, isDark && { color: "#6E6E6E" }]}>
          {hasAppliedFilters
            ? "No Information Matches the Filters Applied, Please Re-Evaluate Filters"
            : "No team members found."}
        </Text>
      </View>
    ) : (
      sortedFilteredMembers.map((member, idx) => {
        const onDuty = member.workingStatus === "On Duty";
        return (
          <View key={member.id} style={[s.row, isDark && { borderBottomColor: "#363636" }, idx % 2 === 0 ? (isDark ? { backgroundColor: "#202020" } : s.rowEven) : (isDark ? { backgroundColor: "#1a1a1a" } : s.rowOdd)]}>
            {/* Avatar + Name */}
            <View style={[s.cell, s.colName]}>
              <View style={[s.avatar, { backgroundColor: member.avatarColor }]}>
                <Text style={s.avatarText}>{member.initials}</Text>
              </View>
              <Text style={[s.memberName, isDark && { color: "#F2F2F2" }]} numberOfLines={1}>{member.name}</Text>
            </View>

            {/* Email */}
            <View style={[s.cell, s.colEmail]}>
              <Text style={[s.cellText, isDark && { color: "#B0B0B0" }]} numberOfLines={1}>{member.email}</Text>
            </View>

            {/* Role badge */}
            <View style={[s.cell, s.colRole]}>
              <View style={[
                s.roleBadge,
                member.role === "lead" ? s.roleLead : s.roleMember,
                isDark && { backgroundColor: member.role === "lead" ? colors.background.elevated : colors.statusBackground.info },
              ]}>
                <Text style={[
                  s.roleBadgeText,
                  member.role === "lead" ? s.roleLeadText : s.roleMemberText,
                  isDark && { color: member.role === "lead" ? colors.text.primary : colors.brand.primary },
                ]}>
                  {member.role === "lead" ? "Lead" : "Member"}
                </Text>
              </View>
            </View>

            {/* Manager */}
            <View style={[s.cell, s.colManager]}>
              <Text style={[s.cellText, isDark && { color: "#B0B0B0" }]} numberOfLines={1}>
                {member.managerEmail ? formatUserName(member.managerEmail) : "—"}
              </Text>
            </View>

            {/* Team */}
            <View style={[s.cell, s.colTeam]}>
              {member.teamId != null ? (() => {
                const teamName = teamNameById.get(String(member.teamId));
                const teamColor = teamColorById.get(String(member.teamId)) ?? "#838383";
                const teamInactive = !availableTeamIds.has(String(member.teamId));
                return teamName ? (
                  <View style={[s.teamPill, { backgroundColor: teamInactive ? "#F0F0F0" : `${teamColor}26` }]}>
                    <View style={[s.teamDot, { backgroundColor: teamInactive ? "#9A9A9A" : teamColor }]} />
                    <Text style={[s.teamPillText, { color: teamInactive ? "#777777" : teamColor }]} numberOfLines={1}>
                      {teamInactive ? `${teamName} - Inactive` : teamName}
                    </Text>
                  </View>
                ) : (
                  <Text style={[s.cellText, isDark && { color: "#B0B0B0" }]}>—</Text>
                );
              })() : (
                <Text style={[s.cellText, isDark && { color: "#B0B0B0" }]}>Unassigned</Text>
              )}
            </View>

            {/* Channels */}
            <View style={[s.cell, s.colChannels, { justifyContent: "center" }]}>
              <ChannelIcons channels={member.channelsOnDuty} isDark={isDark} />
            </View>

            {/* Scope */}
            <View style={[s.cell, s.colScope]}>
              <Text style={[s.cellText, isDark && { color: "#B0B0B0" }]} numberOfLines={1}>{SCOPE_LABEL[member.scope]}</Text>
            </View>

            {/* Status */}
            <View style={[s.cell, s.colStatus, { justifyContent: "center" }]}>
              <View style={[
                s.statusBadge,
                onDuty ? s.statusOn : s.statusOff,
                isDark && { backgroundColor: onDuty ? colors.statusBackground.success : colors.background.elevated },
              ]}>
                <View style={[
                  s.statusDot,
                  onDuty ? s.statusDotOn : s.statusDotOff,
                  isDark && { backgroundColor: onDuty ? colors.status.success : colors.text.disabled },
                ]} />
                <Text style={[
                  s.statusText,
                  onDuty ? s.statusOnText : s.statusOffText,
                  isDark && { color: onDuty ? colors.status.success : colors.text.secondary },
                ]} numberOfLines={1}>
                  {onDuty ? "On Duty" : "Off Duty"}
                </Text>
              </View>
            </View>

            {/* Edit button */}
            <View style={[s.cell, s.colActions, { justifyContent: "flex-end" }]}>
              {canEditMember(member) ? (
                <Pressable
                  style={({ pressed }) => [s.editBtn, isDark && { backgroundColor: "#202020", borderColor: "#404040" }, pressed && s.editBtnPressed, pressed && isDark && { backgroundColor: colors.statusBackground.info }]}
                  onPress={() => onOpenEdit(member)}
                >
                  <MaterialIcons name="edit" size={13} color={isDark ? "#3D8BFF" : "#034895"} />
                  <Text style={[s.editBtnText, isDark && { color: "#3D8BFF" }]}>Edit</Text>
                </Pressable>
              ) : (
                <Text style={[s.cellText, isDark && { color: "#6E6E6E" }, { opacity: 0.35 }]}>—</Text>
              )}
            </View>
          </View>
        );
      })
    )}
    </View>
    </ScrollView>
  </View>
  );
};

export default TeamTable;
